/**
 * /bookingflight/payment — Mock Payment (Phase 9).
 *
 * Presents payment methods (Credit/Debit Card, JazzCash, Easypaisa, Bank Transfer) with
 * method-specific fields and light validation. On submit it runs the mock processPayment,
 * then createBooking, stores the confirmed booking in context, and routes to the
 * confirmation page. No real gateway is contacted.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { processPayment, createBooking } from "../../mock/api";
import { useBooking } from "../../context/BookingContext";
import { formatMoney } from "../../utils/fare";
import "./payment.css";

const METHODS = [
  { key: "credit", label: "Credit Card", icon: "fas fa-credit-card" },
  { key: "debit", label: "Debit Card", icon: "fas fa-money-check-alt" },
  { key: "jazzcash", label: "JazzCash", icon: "fas fa-mobile-alt" },
  { key: "easypaisa", label: "Easypaisa", icon: "fas fa-wallet" },
  { key: "bank", label: "Bank Transfer", icon: "fas fa-university" },
];

const isCard = (m) => m === "credit" || m === "debit";
const isWallet = (m) => m === "jazzcash" || m === "easypaisa";

export default function Payment() {
  const navigate = useNavigate();
  const {
    searchCriteria,
    selectedFlight,
    passengers,
    contact,
    fare,
    setConfirmation,
  } = useBooking();

  const [method, setMethod] = useState("credit");
  const [processing, setProcessing] = useState(false);
  const [fields, setFields] = useState({
    cardNumber: "",
    cardName: "",
    expiry: "",
    cvv: "",
    walletNumber: "",
    walletCnic: "",
  });
  const [errors, setErrors] = useState({});

  const methodLabel = useMemo(
    () => METHODS.find((m) => m.key === method)?.label || "",
    [method]
  );

  const setField = (key, value) => {
    setFields((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (isCard(method)) {
      const digits = fields.cardNumber.replace(/\s/g, "");
      if (digits.length < 15 || digits.length > 16) next.cardNumber = "Enter a valid card number.";
      if (!fields.cardName.trim()) next.cardName = "Name on card is required.";
      if (!/^\d{2}\/\d{2}$/.test(fields.expiry)) next.expiry = "Use MM/YY.";
      if (!/^\d{3,4}$/.test(fields.cvv)) next.cvv = "Invalid CVV.";
    } else if (isWallet(method)) {
      if (!/^03\d{9}$/.test(fields.walletNumber.replace(/[\s-]/g, "")))
        next.walletNumber = "Enter a valid mobile number (03XXXXXXXXX).";
      if (fields.walletCnic && !/^\d{4}$/.test(fields.walletCnic))
        next.walletCnic = "Enter the last 4 digits of your CNIC.";
    }
    // Bank transfer: no fields to validate (mocked confirmation).
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please check your payment details.");
      return;
    }
    setProcessing(true);
    try {
      const payment = await processPayment({
        method: methodLabel,
        amount: fare.total,
        currency: fare.currency,
      });

      const booking = await createBooking({
        flight: selectedFlight,
        passengers,
        contact,
        fare,
        paymentMethod: methodLabel,
        transactionId: payment.transactionId,
        createdAt: payment.paidAt,
        searchCriteria,
      });

      setConfirmation(booking);
      toast.success("Payment successful!");
      navigate("/bookingflight/confirmation");
    } catch (err) {
      toast.error("Payment could not be completed. Please try again.");
      setProcessing(false);
    }
  };

  const formatCard = (v) =>
    v.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();

  const formatExpiry = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 4);
    return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
  };

  return (
    <section className="pay-page">
      <div className="pay-header">
        <div className="pay-header__inner">
          <button type="button" className="pay-back" onClick={() => navigate(-1)} disabled={processing}>
            <i className="fas fa-arrow-left" aria-hidden="true" /> Back to review
          </button>
          <h1 className="pay-title">Payment</h1>
          <p className="pay-subtitle">
            <i className="fas fa-lock" aria-hidden="true" /> Secure mock checkout — no real charge.
          </p>
        </div>
      </div>

      <div className="pay-body">
        <form className="pay-main" onSubmit={handlePay}>
          <h2 className="pay-section-title">Choose a payment method</h2>
          <div className="pay-methods">
            {METHODS.map((m) => (
              <button
                type="button"
                key={m.key}
                className={`pay-method ${method === m.key ? "pay-method--active" : ""}`}
                onClick={() => setMethod(m.key)}
                disabled={processing}
              >
                <i className={m.icon} aria-hidden="true" />
                <span>{m.label}</span>
              </button>
            ))}
          </div>

          <div className="pay-fields">
            {isCard(method) && (
              <>
                <div className="pay-field pay-field--wide">
                  <label>Card Number</label>
                  <input
                    inputMode="numeric"
                    value={fields.cardNumber}
                    onChange={(e) => setField("cardNumber", formatCard(e.target.value))}
                    placeholder="1234 5678 9012 3456"
                    className={errors.cardNumber ? "pay-input pay-input--error" : "pay-input"}
                  />
                  {errors.cardNumber && <span className="pay-error">{errors.cardNumber}</span>}
                </div>
                <div className="pay-field pay-field--wide">
                  <label>Name on Card</label>
                  <input
                    value={fields.cardName}
                    onChange={(e) => setField("cardName", e.target.value)}
                    placeholder="AHMED ALI"
                    className={errors.cardName ? "pay-input pay-input--error" : "pay-input"}
                  />
                  {errors.cardName && <span className="pay-error">{errors.cardName}</span>}
                </div>
                <div className="pay-field">
                  <label>Expiry (MM/YY)</label>
                  <input
                    inputMode="numeric"
                    value={fields.expiry}
                    onChange={(e) => setField("expiry", formatExpiry(e.target.value))}
                    placeholder="08/28"
                    className={errors.expiry ? "pay-input pay-input--error" : "pay-input"}
                  />
                  {errors.expiry && <span className="pay-error">{errors.expiry}</span>}
                </div>
                <div className="pay-field">
                  <label>CVV</label>
                  <input
                    inputMode="numeric"
                    type="password"
                    value={fields.cvv}
                    onChange={(e) => setField("cvv", e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="123"
                    className={errors.cvv ? "pay-input pay-input--error" : "pay-input"}
                  />
                  {errors.cvv && <span className="pay-error">{errors.cvv}</span>}
                </div>
              </>
            )}

            {isWallet(method) && (
              <>
                <div className="pay-note-box">
                  <i className={method === "jazzcash" ? "fas fa-mobile-alt" : "fas fa-wallet"} aria-hidden="true" />
                  <span>
                    You'll receive a mock {methodLabel} prompt on your registered mobile number to
                    approve {formatMoney(fare.total, fare.currency)}.
                  </span>
                </div>
                <div className="pay-field">
                  <label>Mobile Account Number</label>
                  <input
                    inputMode="numeric"
                    value={fields.walletNumber}
                    onChange={(e) => setField("walletNumber", e.target.value.replace(/[^\d]/g, "").slice(0, 11))}
                    placeholder="03001234567"
                    className={errors.walletNumber ? "pay-input pay-input--error" : "pay-input"}
                  />
                  {errors.walletNumber && <span className="pay-error">{errors.walletNumber}</span>}
                </div>
                <div className="pay-field">
                  <label>CNIC last 4 (optional)</label>
                  <input
                    inputMode="numeric"
                    value={fields.walletCnic}
                    onChange={(e) => setField("walletCnic", e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="1234"
                    className={errors.walletCnic ? "pay-input pay-input--error" : "pay-input"}
                  />
                  {errors.walletCnic && <span className="pay-error">{errors.walletCnic}</span>}
                </div>
              </>
            )}

            {method === "bank" && (
              <div className="pay-bank">
                <div className="pay-note-box">
                  <i className="fas fa-university" aria-hidden="true" />
                  <span>Transfer the total to the account below. This mock confirms instantly.</span>
                </div>
                <dl className="pay-bank__details">
                  <div><dt>Bank</dt><dd>Falcon Mock Bank Ltd.</dd></div>
                  <div><dt>Account Title</dt><dd>Falcon Destinations (Pvt.) Ltd</dd></div>
                  <div><dt>Account Number</dt><dd>0123-4567-8901-2345</dd></div>
                  <div><dt>IBAN</dt><dd>PK00 FALC 0000 0123 4567 8901</dd></div>
                  <div><dt>Amount</dt><dd>{formatMoney(fare.total, fare.currency)}</dd></div>
                </dl>
              </div>
            )}
          </div>
        </form>

        {/* Order summary */}
        <aside className="pay-aside">
          <div className="pay-summary">
            <h2 className="pay-summary__title">Order Summary</h2>
            <div className="pay-summary__flight">
              <img src={selectedFlight.airlineLogo} alt={selectedFlight.airline} width={32} height={32} />
              <div>
                <div className="pay-summary__airline">{selectedFlight.airline}</div>
                <div className="pay-summary__route">
                  {selectedFlight.departureAirport.code} → {selectedFlight.arrivalAirport.code} · {selectedFlight.flightNumber}
                </div>
              </div>
            </div>
            <div className="pay-summary__rows">
              <div><span>Base Fare</span><span>{formatMoney(fare.baseFare, fare.currency)}</span></div>
              <div><span>Taxes</span><span>{formatMoney(fare.taxes, fare.currency)}</span></div>
              <div><span>Service Fee</span><span>{formatMoney(fare.serviceFee, fare.currency)}</span></div>
            </div>
            <div className="pay-summary__total">
              <span>Total</span>
              <span className="pay-summary__total-value">{formatMoney(fare.total, fare.currency)}</span>
            </div>

            <button
              type="button"
              className="pay-btn"
              onClick={handlePay}
              disabled={processing}
            >
              {processing ? (
                <><span className="pay-btn__spinner" aria-hidden="true" /> Processing…</>
              ) : (
                <>Pay {formatMoney(fare.total, fare.currency)}</>
              )}
            </button>
            <p className="pay-summary__note">
              By paying you agree to the mock booking terms.
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}
